import React, { FC, useContext, useState } from 'react'
import { UserSettingsInput } from '../components/UserSettingsInput'
import { useDispatch, useSelector } from 'react-redux'
import { AppState, Product, encryptPass } from '@news-mono/web-common'
import { UserSettingsButton } from '../components/UserSettingsButton'
import { useChangeEmail } from '../hooks/useChangeEmail'
import { MyAccountContext } from '../hooks/useMyAccountContext'
import { UserSettingsLink } from '../components/UserSettingsLink'
import { tokens } from '@news-mono/design-tokens'
import { metrics } from '../../__styling'
import { UserSettingProps } from '../UserSetting'
import { useForgotPassword } from '../hooks/useChangePassword'
import { BannerHelperProps } from './helpers/change-email-banner-helper'
import {
    ClickHelperProps,
    onChangeEmailFormCancelHelper,
    onChangeEmailFormSubmitHandler,
    onForgotPasswordClick,
} from './helpers/change-email-click-helper'
import { useSendVerifyEmail } from '../hooks/useSendVerifyEmail'
import { StyledUserSettingsForm } from '../components/UserSettingsForm.styled'
import { useProduct } from '../../__product'
import { StyledButtonGroup } from '../components/UserSettingsButtonGroup.styled'

// Stores the current email, userId & password that gets typed into the fields.
export interface ChangeEmailFormProps {
    email: string
    userId: string
    currentPassword: string
    newEmail: string
}
// Used to store which field is erroring out
export interface ChangeEmailErrorProps {
    emailError: boolean
    passwordError: boolean
}

// TheNightly Colors
const tnColors = tokens.thenightly.colors
const tnPalette = tnColors.palette
// TheNightly Metrics
const tnMetrics = metrics.thenightly
const tnMargins = tnMetrics.margins

// PerthNow Colors
const pnColors = tokens.perthnow.colors
const pnPalette = pnColors.palette

export interface ChangeEmailProps extends UserSettingProps {}

export const ChangeEmail: FC<ChangeEmailProps> = ({ onEvent, log, config }) => {
    // Pass through context before anything
    const myAccountContext = useContext(MyAccountContext)
    const dispatch = useDispatch()
    // Select the user email & wan user id from the current authenticated state
    const { userEmail, wanUserId, auth0UserId } = useSelector(
        (state: AppState) => state.authentication,
    )
    const userId = auth0UserId || wanUserId
    // Store the values utilised to proceed with the email change
    const [emailChangeProps, setEmailChangeProps] =
        useState<ChangeEmailFormProps>({
            email: userEmail || '',
            userId: userId,
            currentPassword: '',
            newEmail: userEmail || '',
        })
    // Store the submitting and error values
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [errors, setErrors] = useState<ChangeEmailErrorProps>({
        emailError: false,
        passwordError: false,
    })
    const product = useProduct()
    const isPn = product === Product.PerthNow

    // Stores the backend handlers below
    const { mutateAsync: sendForgotPasswordEmail } = useForgotPassword({
        email: userEmail!,
    })
    const { mutateAsync: sendEmailChangeUpdate } =
        useChangeEmail(emailChangeProps)
    const { mutateAsync: sendVerifyEmail } = useSendVerifyEmail(userId)

    // Stores all external helper props below
    const bannerProps: BannerHelperProps = {
        myAccountContext,
        errorState: { errors, setErrors },
        dispatch,
        log,
        config,
        onEvent,
    }
    const clickProps: ClickHelperProps = {
        myAccountContext,
        mutateProps: {
            sendEmailChangeUpdate,
            sendForgotPasswordEmail,
            sendVerifyEmail,
        },
        emailChangeState: {
            emailChangeProps,
            setEmailChangeProps,
        },
        submittingState: {
            submitting,
            setSubmitting,
        },
    }

    // Builds the component
    return (
        <StyledUserSettingsForm autoComplete={'off'}>
            <UserSettingsInput
                id="email"
                type="edit"
                label="Email Address"
                value={userEmail}
                hasError={errors.emailError}
                onChange={(text) => {
                    setEmailChangeProps({
                        ...emailChangeProps,
                        newEmail: text,
                    })
                }}
                onSelect={() => setErrors({ ...errors, emailError: false })}
            />
            <UserSettingsInput
                id="password"
                type="password"
                label="Enter Password"
                hasError={errors.passwordError}
                onChange={async (text) => {
                    const encryptedPass =
                        (await encryptPass(
                            text,
                            config.auth0?.passEncryptionKey,
                        )) || ''
                    setEmailChangeProps({
                        ...emailChangeProps,
                        currentPassword: encryptedPass,
                    })
                }}
                onSelect={() => setErrors({ ...errors, passwordError: false })}
                onEnter={(e) =>
                    onChangeEmailFormSubmitHandler(clickProps, bannerProps, e)
                }
                marginBottom={isPn ? undefined : tnMargins.md}
            />
            <UserSettingsLink
                color={
                    isPn
                        ? pnPalette.pinkThulite
                        : tnPalette.section.opinion.default
                }
                onClick={() => onForgotPasswordClick(clickProps, bannerProps)}
                display={'flex'}
                width={'fit-content'}
                marginBottom={tnMargins.lg}
            >
                Forgot your password?
            </UserSettingsLink>
            <StyledButtonGroup>
                <UserSettingsButton
                    onClick={(e) =>
                        onChangeEmailFormCancelHelper(clickProps, e)
                    }
                    btnType="muted"
                >
                    Cancel
                </UserSettingsButton>
                <UserSettingsButton
                    onClick={(e) =>
                        onChangeEmailFormSubmitHandler(
                            clickProps,
                            bannerProps,
                            e,
                        )
                    }
                    btnType="primary"
                    disabled={submitting}
                >
                    Save
                </UserSettingsButton>
            </StyledButtonGroup>
        </StyledUserSettingsForm>
    )
}
